import "@/App.css";
import { lazy, Suspense } from "react";
import { InstallHint } from "@/components/common/InstallHint";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then((m) => ({ default: m.AppLayout })));
import LoginPage from "@/pages/LoginPage";
const OrderBook = lazy(() => import("@/pages/purchases/OrderBook"));
const PartyProfile = lazy(() => import("@/pages/parties/PartyProfile"));
const CreditControl = lazy(() => import("@/pages/billing/CreditControl"));
const AccountingReport = lazy(() => import("@/pages/accounting/Reports"));
const GstReturn = lazy(() => import("@/pages/accounting/GstReturns"));
const ChartOfAccounts = lazy(() => import("@/pages/accounting/ChartOfAccounts"));
const VoucherPage = lazy(() => import("@/pages/accounting/Voucher"));
const AccountingSettings = lazy(() => import("@/pages/accounting/AccountingSettings"));
const Reorder = lazy(() => import("@/pages/purchases/Reorder"));
import Landing from "@/pages/Landing";
const Signup = lazy(() => import("@/pages/Signup"));
const OwnerPanel = lazy(() => import("@/pages/owner/OwnerPanel"));
const AccessRoles = lazy(() => import("@/pages/settings/AccessRoles"));
const BusinessProfile = lazy(() => import("@/pages/settings/BusinessProfile"));
const ImportCenter = lazy(() => import("@/pages/settings/ImportCenter"));
const ImportJob = lazy(() => import("@/pages/settings/ImportJob"));
const MyPlan = lazy(() => import("@/pages/settings/MyPlan"));
import { canView } from "@/lib/menu";
import { useLocation } from "react-router-dom";
import { ShieldOff } from "lucide-react";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const EmployeeList = lazy(() => import("@/pages/EmployeeList"));
const AddEmployee = lazy(() => import("@/pages/AddEmployee"));
const EmployeeView = lazy(() => import("@/pages/EmployeeView"));
const EmployeeManage = lazy(() => import("@/pages/EmployeeManage"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const DocumentsList = lazy(() => import("@/pages/DocumentsList"));
const DocumentSettings = lazy(() => import("@/pages/DocumentSettings"));
const OrganizationSetup = lazy(() => import("@/pages/OrganizationSetup"));
const EmployeeHome = lazy(() => import("@/pages/EmployeeHome"));
const AttendanceRecords = lazy(() => import("@/pages/attendance/AttendanceRecords"));
const AttendanceSummary = lazy(() => import("@/pages/attendance/AttendanceRecords").then((m) => ({ default: m.AttendanceSummary })));
const AttendanceSettings = lazy(() => import("@/pages/attendance/AttendanceSettings"));
const ManualOT = lazy(() => import("@/pages/attendance/AttendanceSettings").then((m) => ({ default: m.ManualOT })));
const RegularizationRequests = lazy(() => import("@/pages/attendance/AttendanceSettings").then((m) => ({ default: m.RegularizationRequests })));
const LeaveRequests = lazy(() => import("@/pages/leave/LeavePages"));
const AssignLeave = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.AssignLeave })));
const Holidays = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.Holidays })));
const Entitlements = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.Entitlements })));
const BalanceHistory = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.BalanceHistory })));
const LeaveReport = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.LeaveReport })));
const LeaveSettings = lazy(() => import("@/pages/leave/LeavePages").then((m) => ({ default: m.LeaveSettings })));
const PayrollSettings = lazy(() => import("@/pages/payroll/Payroll"));
const SalaryStructures = lazy(() => import("@/pages/payroll/Payroll").then((m) => ({ default: m.SalaryStructures })));
const StructureEditor = lazy(() => import("@/pages/payroll/Payroll").then((m) => ({ default: m.StructureEditor })));
const PayRuns = lazy(() => import("@/pages/payroll/Payroll").then((m) => ({ default: m.PayRuns })));
const PayRunView = lazy(() => import("@/pages/payroll/Payroll").then((m) => ({ default: m.PayRunView })));
const MyPayslips = lazy(() => import("@/pages/payroll/Payroll").then((m) => ({ default: m.MyPayslips })));
const Targets = lazy(() => import("@/pages/targets/Targets"));
const MyTarget = lazy(() => import("@/pages/targets/Targets").then((m) => ({ default: m.MyTarget })));
const AlertSettings = lazy(() => import("@/pages/targets/Targets").then((m) => ({ default: m.AlertSettings })));
const MyAttendance = lazy(() => import("@/pages/me/SelfService"));
const MyLeave = lazy(() => import("@/pages/me/SelfService").then((m) => ({ default: m.MyLeave })));
const MyRegularizations = lazy(() => import("@/pages/me/SelfService").then((m) => ({ default: m.MyRegularizations })));
const PendingMyAction = lazy(() => import("@/pages/me/SelfService").then((m) => ({ default: m.PendingMyAction })));
const LocationsList = lazy(() => import("@/pages/LocationsList"));
const LocationForm = lazy(() => import("@/pages/LocationForm"));
const LocationCategories = lazy(() => import("@/pages/LocationCategories"));
const Punches = lazy(() => import("@/pages/Punches"));
const WhosIn = lazy(() => import("@/pages/WhosIn"));
const MobileCheckinReport = lazy(() => import("@/pages/MobileCheckinReport"));
const PunchSettings = lazy(() => import("@/pages/PunchSettings"));
const CrmDashboard = lazy(() => import("@/pages/crm/CrmDashboard"));
const Leads = lazy(() => import("@/pages/crm/Leads"));
const LeadDetail = lazy(() => import("@/pages/crm/LeadDetail"));
const Pipeline = lazy(() => import("@/pages/crm/Pipeline"));
const FollowUps = lazy(() => import("@/pages/crm/FollowUps"));
const CrmSettings = lazy(() => import("@/pages/crm/CrmSettings"));
const DocList = lazy(() => import("@/pages/billing/DocList"));
const DocForm = lazy(() => import("@/pages/billing/DocForm"));
const DocView = lazy(() => import("@/pages/billing/DocView"));
const Payments = lazy(() => import("@/pages/billing/Payments"));
const Outstanding = lazy(() => import("@/pages/billing/Outstanding"));
const Products = lazy(() => import("@/pages/billing/Products"));
const BillingSettings = lazy(() => import("@/pages/billing/BillingSettings"));
const Parties = lazy(() => import("@/pages/parties/Parties"));
const Connections = lazy(() => import("@/pages/parties/Connections"));
const PaymentsOut = lazy(() => import("@/pages/purchases/PaymentsOut"));
const Expenses = lazy(() => import("@/pages/purchases/Expenses"));
const Transfers = lazy(() => import("@/pages/purchases/Transfers"));
const Stock = lazy(() => import("@/pages/purchases/Stock"));
const PartyLedger = lazy(() => import("@/pages/purchases/PartyLedger"));
const BillInbox = lazy(() => import("@/pages/purchases/BillInbox"));
const OrderRequests = lazy(() => import("@/pages/billing/OrderRequests"));
const LeadCapture = lazy(() => import("@/pages/crm/LeadCapture"));
const LeadForm = lazy(() => import("@/pages/public/LeadForm"));
import { PortalProvider } from "@/context/PortalContext";
import PublicCatalog from "@/pages/public/PublicCatalog";
const PortalLayout = lazy(() => import("@/components/portal/PortalLayout").then((m) => ({ default: m.PortalLayout })));
const PortalMagic = lazy(() => import("@/pages/portal/PortalMagic"));
const PortalHome = lazy(() => import("@/pages/portal/PortalHome"));
const ClientQuotations = lazy(() => import("@/pages/portal/ClientQuotations"));
const ClientInvoices = lazy(() => import("@/pages/portal/ClientInvoices"));
const ClientOrders = lazy(() => import("@/pages/portal/ClientOrders"));
const PortalStatement = lazy(() => import("@/pages/portal/PortalStatement"));
const VendorOrders = lazy(() => import("@/pages/portal/VendorPages").then((m) => ({ default: m.VendorOrders })));
const VendorBills = lazy(() => import("@/pages/portal/VendorPages").then((m) => ({ default: m.VendorBills })));
const VendorUpload = lazy(() => import("@/pages/portal/VendorPages").then((m) => ({ default: m.VendorUpload })));

function PortalRoot() {
  return <PortalProvider><Outlet /></PortalProvider>;
}

function Protected() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500" data-testid="app-loading">Loading...</div>;
  if (!user) return pathname === "/" ? <Landing /> : <Navigate to="/login" replace />;
  return <AppLayout />;
}

function NoAccess() {
  return (
    <div data-testid="no-access" className="max-w-md mx-auto mt-24 text-center">
      <ShieldOff className="w-10 h-10 text-slate-300 mx-auto" />
      <p className="font-display text-xl font-bold text-[#262357] mt-4">You don't have access to this</p>
      <p className="text-sm text-slate-500 mt-1">Ask your administrator to change your access role.</p>
    </div>
  );
}

function AdminOnly() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (pathname.startsWith("/coming-soon")) return user.is_admin ? <Outlet /> : <NoAccess />;
  return canView(user, pathname) ? <Outlet /> : <NoAccess />;
}

function Suspended() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb] p-6" data-testid="suspended-page">
      <div className="max-w-md text-center">
        <p className="font-display text-2xl font-bold text-[#262357]">This account is suspended. Please contact BusinessBook support.</p>
        <a href="/login" className="inline-block mt-6 text-sm font-semibold text-teal-700">Back to login</a>
      </div>
    </div>
  );
}

function Home() {
  const { user } = useAuth();
  return user.is_admin || user.is_staff ? <Dashboard /> : <EmployeeHome />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div data-testid="route-loading" className="min-h-screen grid place-items-center"><span className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#262357] animate-spin" /></div>}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/catalog/:token" element={<PublicCatalog />} />
            <Route path="/owner" element={<OwnerPanel />} />
            <Route path="/suspended" element={<Suspended />} />
            <Route path="/lead-form" element={<LeadForm />} />
            <Route path="/portal" element={<PortalRoot />}>
              <Route path="login" element={<Navigate to="/login" replace />} />
              <Route path="magic" element={<PortalMagic />} />
              <Route element={<PortalLayout />}>
                <Route index element={<PortalHome />} />
                <Route path="quotations" element={<ClientQuotations />} />
                <Route path="invoices" element={<ClientInvoices />} />
                <Route path="orders" element={<ClientOrders />} />
                <Route path="statement" element={<PortalStatement />} />
                <Route path="purchase-orders" element={<VendorOrders />} />
                <Route path="bills" element={<VendorBills />} />
                <Route path="upload" element={<VendorUpload />} />
              </Route>
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Route>
            <Route element={<Protected />}>
              <Route index element={<Home />} />
              <Route path="me/attendance" element={<MyAttendance />} />
              <Route path="me/leave" element={<MyLeave />} />
              <Route path="me/regularizations" element={<MyRegularizations />} />
              <Route path="me/approvals" element={<PendingMyAction />} />
              <Route path="me/payslips" element={<MyPayslips />} />
              <Route path="me/target" element={<MyTarget />} />
              <Route element={<AdminOnly />}>
                <Route path="payroll/settings" element={<PayrollSettings />} />
                <Route path="payroll/structures" element={<SalaryStructures />} />
                <Route path="payroll/structures/:id" element={<StructureEditor />} />
                <Route path="payroll/runs" element={<PayRuns />} />
                <Route path="payroll/runs/:id" element={<PayRunView />} />
                <Route path="targets" element={<Targets />} />
                <Route path="alerts/settings" element={<AlertSettings />} />
                <Route path="attendance/records" element={<AttendanceRecords />} />
                <Route path="attendance/summary" element={<AttendanceSummary />} />
                <Route path="attendance/regularizations" element={<RegularizationRequests />} />
                <Route path="attendance/ot" element={<ManualOT />} />
                <Route path="attendance/settings" element={<AttendanceSettings />} />
                <Route path="leave/requests" element={<LeaveRequests />} />
                <Route path="leave/assign" element={<AssignLeave />} />
                <Route path="leave/holidays" element={<Holidays />} />
                <Route path="leave/entitlements" element={<Entitlements />} />
                <Route path="leave/history" element={<BalanceHistory />} />
                <Route path="leave/report" element={<LeaveReport />} />
                <Route path="leave/settings" element={<LeaveSettings />} />
                <Route path="employees" element={<EmployeeList />} />
                <Route path="employees/new" element={<AddEmployee />} />
                <Route path="employees/:id" element={<EmployeeView />} />
                <Route path="employees/:id/manage" element={<EmployeeManage />} />
                <Route path="organization" element={<OrganizationSetup />} />
                <Route path="documents/personal" element={<DocumentsList category="personal" />} />
                <Route path="documents/dependants" element={<DocumentsList category="dependant" />} />
                <Route path="documents/settings" element={<DocumentSettings />} />
                <Route path="locations" element={<LocationsList />} />
                <Route path="locations/new" element={<LocationForm />} />
                <Route path="locations/categories" element={<LocationCategories />} />
                <Route path="locations/:id/edit" element={<LocationForm key="edit" />} />
                <Route path="punches" element={<Punches />} />
                <Route path="punches/settings" element={<PunchSettings />} />
                <Route path="whos-in" element={<WhosIn />} />
                <Route path="reports/mobile-checkin" element={<MobileCheckinReport />} />
                <Route path="crm" element={<CrmDashboard />} />
                <Route path="crm/leads" element={<Leads />} />
                <Route path="crm/leads/:id" element={<LeadDetail />} />
                <Route path="crm/pipeline" element={<Pipeline />} />
                <Route path="crm/follow-ups" element={<FollowUps />} />
                <Route path="crm/customers" element={<Parties key="customer" role="customer" />} />
                <Route path="crm/customers/:id" element={<PartyProfile />} />
                <Route path="parties" element={<Parties key="all" role="all" />} />
                <Route path="parties/connections" element={<Connections />} />
                <Route path="parties/:id" element={<PartyProfile />} />
                <Route path="billing/credit-control" element={<CreditControl />} />
                <Route path="accounting/day-book" element={<AccountingReport key="day-book" kind="day-book" />} />
                <Route path="accounting/cash-book" element={<AccountingReport key="cash-book" kind="cash-book" />} />
                <Route path="accounting/bank-book" element={<AccountingReport key="bank-book" kind="bank-book" />} />
                <Route path="accounting/ledger" element={<AccountingReport key="ledger" kind="ledger" />} />
                <Route path="accounting/trial-balance" element={<AccountingReport key="trial-balance" kind="trial-balance" />} />
                <Route path="accounting/profit-loss" element={<AccountingReport key="profit-loss" kind="profit-loss" />} />
                <Route path="accounting/balance-sheet" element={<AccountingReport key="balance-sheet" kind="balance-sheet" />} />
                <Route path="accounting/gstr1" element={<GstReturn key="gstr1" kind="gstr1" />} />
                <Route path="accounting/gstr3b" element={<GstReturn key="gstr3b" kind="gstr3b" />} />
                <Route path="accounting/accounts" element={<ChartOfAccounts />} />
                <Route path="accounting/vouchers/:id" element={<VoucherPage />} />
                <Route path="accounting/settings" element={<AccountingSettings />} />
                <Route path="crm/settings" element={<CrmSettings />} />
                <Route path="crm/settings/lead-capture" element={<LeadCapture />} />
                <Route path="billing/quotations" element={<DocList key="ql" kind="quotation" />} />
                <Route path="billing/quotations/new" element={<DocForm key="qn" kind="quotation" />} />
                <Route path="billing/quotations/:id" element={<DocView key="qv" kind="quotation" />} />
                <Route path="billing/quotations/:id/edit" element={<DocForm key="qe" kind="quotation" />} />
                <Route path="billing/invoices" element={<DocList key="il" kind="invoice" />} />
                <Route path="billing/invoices/new" element={<DocForm key="in" kind="invoice" />} />
                <Route path="billing/invoices/:id" element={<DocView key="iv" kind="invoice" />} />
                <Route path="billing/invoices/:id/edit" element={<DocForm key="ie" kind="invoice" />} />
                <Route path="billing/credit-notes" element={<DocList key="cl" kind="credit_note" />} />
                <Route path="billing/credit-notes/:id" element={<DocView key="cv" kind="credit_note" />} />
                <Route path="billing/payments" element={<Payments />} />
                <Route path="billing/outstanding" element={<Outstanding />} />
                <Route path="billing/products" element={<Products />} />
                <Route path="billing/settings" element={<BillingSettings />} />
                <Route path="purchases/vendors" element={<Parties key="vendor" role="vendor" />} />
                <Route path="purchases/order-book" element={<OrderBook />} />
                <Route path="purchases/reorder" element={<Reorder />} />
                <Route path="purchases/orders" element={<DocList key="pol" kind="purchase_order" />} />
                <Route path="purchases/orders/new" element={<DocForm key="pon" kind="purchase_order" />} />
                <Route path="purchases/orders/:id" element={<DocView key="pov" kind="purchase_order" />} />
                <Route path="purchases/orders/:id/edit" element={<DocForm key="poe" kind="purchase_order" />} />
                <Route path="purchases/invoices" element={<DocList key="pil" kind="purchase_invoice" />} />
                <Route path="purchases/invoices/new" element={<DocForm key="pin" kind="purchase_invoice" />} />
                <Route path="purchases/invoices/:id" element={<DocView key="piv" kind="purchase_invoice" />} />
                <Route path="purchases/invoices/:id/edit" element={<DocForm key="pie" kind="purchase_invoice" />} />
                <Route path="purchases/debit-notes" element={<DocList key="dnl" kind="debit_note" />} />
                <Route path="purchases/debit-notes/:id" element={<DocView key="dnv" kind="debit_note" />} />
                <Route path="purchases/payments-out" element={<PaymentsOut />} />
                <Route path="purchases/expenses" element={<Expenses />} />
                <Route path="purchases/transfers" element={<Transfers />} />
                <Route path="purchases/stock" element={<Stock />} />
                <Route path="purchases/ledger" element={<PartyLedger />} />
                <Route path="purchases/bill-inbox" element={<BillInbox />} />
                <Route path="billing/order-requests" element={<OrderRequests />} />
                <Route path="settings/roles" element={<AccessRoles />} />
                <Route path="settings/business-profile" element={<BusinessProfile />} />
                <Route path="settings/imports" element={<ImportCenter />} />
                <Route path="settings/imports/:id" element={<ImportJob />} />
                <Route path="settings/my-plan" element={<MyPlan />} />
                <Route path="coming-soon/:slug" element={<ComingSoon />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <InstallHint />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
